import { AppIcon, type AppIcons, LabeledFilterSelect } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, InputAdornment, type SxProps, TextField, type Theme } from '@mui/material';

const strings = appStrings.modules.rostersAdmin;

/** The un-narrowed value. A filter that reads blank cannot say whether it is narrowing. */
export const ALL = 'all';

export type FilterOption = Readonly<{ value: string; label: string }>;

export const withAll = (allLabel: string, options: readonly FilterOption[]): FilterOption[] => [
  { value: ALL, label: allLabel },
  ...options,
];

type Field = Readonly<{
  id: string;
  label: string;
  iconName: AppIcons;
  value: string;
  options: readonly FilterOption[];
  onChange: (value: string) => void;
}>;

type RostersAdminFilterBarProps = Readonly<{
  institution: string;
  institutionOptions: readonly FilterOption[];
  onInstitutionChange: (value: string) => void;
  /** Only on the cohorts tab, where B1 replaced the per-roster sections with one list. */
  roster?: string;
  rosterOptions?: readonly FilterOption[];
  onRosterChange?: (value: string) => void;
  search?: string;
  onSearchChange?: (value: string) => void;
}>;

export const RostersAdminFilterBar = ({
  institution,
  institutionOptions,
  onInstitutionChange,
  roster,
  rosterOptions,
  onRosterChange,
  search,
  onSearchChange,
}: RostersAdminFilterBarProps) => {
  const fields: Field[] = [
    {
      id: 'rosters-admin-institution-filter',
      label: appStrings.modules.coachingDashboard.institutionAdmin.inScopeFilterLabel,
      iconName: 'Building07',
      value: institution,
      options: withAll(strings.allInstitutions, institutionOptions),
      onChange: onInstitutionChange,
    },
  ];

  if (roster !== undefined && rosterOptions && onRosterChange) {
    fields.push({
      id: 'rosters-admin-roster-filter',
      label: strings.rosterFilterLabel,
      iconName: 'List',
      value: roster,
      options: withAll(strings.allRosters, rosterOptions),
      onChange: onRosterChange,
    });
  }

  return (
    <Box id="rosters-admin-filter-bar" sx={barSx}>
      {search !== undefined && onSearchChange ? (
        <TextField
          id="rosters-admin-cohort-search"
          placeholder={strings.cohortSearchPlaceholder}
          inputProps={{ 'aria-label': strings.cohortSearchLabel }}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          size="small"
          sx={searchSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AppIcon
                  iconName="SearchMd"
                  diameter="20px"
                  color="Colors/Foreground/fg-quaternary"
                />
              </InputAdornment>
            ),
          }}
        />
      ) : null}
      <Box id="rosters-admin-filter-fields" sx={fieldsRowSx}>
        {fields.map((field) => (
          <Box key={field.id} sx={fieldSx}>
            <LabeledFilterSelect
              id={field.id}
              label={field.label}
              iconName={field.iconName}
              value={field.value}
              options={field.options}
              onChange={field.onChange}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const barSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  flexShrink: 0,
  gap: backrs.spacing['spacing-md'],
  justifyContent: 'flex-end',
  marginBottom: backrs.spacing['spacing-md'],
});

const searchSx: SxProps<Theme> = {
  flex: '1 1 16rem',
  maxWidth: '24rem',
  marginRight: 'auto',
};

const fieldsRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-md'],
  minWidth: 0,
});

const FIELD_WIDTH = '15rem';

const fieldSx: SxProps<Theme> = {
  display: 'flex',
  minWidth: 0,
  flex: `0 0 ${FIELD_WIDTH}`,
};
